import { useState, useRef, useEffect, useMemo, useId } from 'react'
import { useMultiSelectKeyDown } from '@/components/ui/multi-select/useMultiSelectKeyDown'

export interface Option {
  value: string
  label: string
}

/** Options narrowed by the search text, plus the selected values resolved to labels. */
function useOptionViews(options: Option[], search: string, value: string[]) {
  const filtered = useMemo(() => {
    if (!search) return options
    const lower = search.toLowerCase()
    return options.filter((o) => o.label.toLowerCase().includes(lower))
  }, [options, search])

  const selectedLabels = useMemo(() => {
    const map = new Map(options.map((o) => [o.value, o.label]))
    return value.map((v) => ({ value: v, label: map.get(v) ?? v }))
  }, [value, options])

  return { filtered, selectedLabels }
}

/** State, effects and keyboard handling for the MultiSelect combobox (WAI-ARIA listbox pattern). */
export function useMultiSelect(value: string[], onChange: (value: string[]) => void, options: Option[]) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  // Index of the option highlighted via keyboard (aria-activedescendant). -1 = none.
  const [activeIndex, setActiveIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const baseId = useId()
  const listboxId = `${baseId}-listbox`
  const optionId = (index: number) => `${baseId}-option-${index}`

  const { filtered, selectedLabels } = useOptionViews(options, search, value)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Reset the active option to the top whenever the list opens or the query changes.
  useEffect(() => {
    if (!open) return
    setActiveIndex(filtered.length > 0 ? 0 : -1)
  }, [open, search, filtered.length])

  // Keep the keyboard-highlighted option scrolled into view.
  useEffect(() => {
    if (!open || activeIndex < 0) return
    document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: 'nearest' })
    // optionId is derived from a stable id; activeIndex/open are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeIndex])

  const toggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
    setSearch('')
    inputRef.current?.focus()
  }

  const removeTag = (optionValue: string) => {
    onChange(value.filter((v) => v !== optionValue))
  }

  const close = () => {
    setOpen(false)
    setSearch('')
  }

  const handleKeyDown = useMultiSelectKeyDown({
    open,
    setOpen,
    search,
    filtered,
    activeIndex,
    setActiveIndex,
    value,
    toggle,
    removeTag,
    close,
  })

  return {
    open,
    setOpen,
    search,
    setSearch,
    activeIndex,
    setActiveIndex,
    containerRef,
    inputRef,
    listboxId,
    optionId,
    filtered,
    selectedLabels,
    toggle,
    removeTag,
    handleKeyDown,
  }
}
